import { GoogleGenAI, Type } from "@google/genai";
import { Question, Attempt, AIAnalysis } from '../types';

// Debug flag - set to true to see logs
const DEBUG = true;

const getAIClient = () => {
  const apiKey = process.env.API_KEY;

  if (DEBUG) {
    console.log('API Key present:', !!apiKey);
    console.log('API Key length:', apiKey?.length || 0);
  }

  if (!apiKey) {
    throw new Error('VITE_API_KEY is not configured. Please add it to your environment variables.');
  }

  return new GoogleGenAI({ apiKey });
};

// Timeout wrapper for API calls
const withTimeout = <T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> => {
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(errorMessage)), ms)
  );
  return Promise.race([promise, timeout]);
};

interface AdvancedOptions {
  examType?: string;
  language?: string;
  questionStyle?: string;
  includeCurrentAffairs?: boolean;
  yearRange?: string;
  specificInstructions?: string;
}

export const generateQuestionsAI = async (
  subject: string,
  topic: string,
  count: number,
  difficulty: string,
  advancedOptions?: AdvancedOptions | null
): Promise<Question[]> => {
  if (DEBUG) console.log('Starting AI generation...', { subject, topic, count, difficulty });

  const ai = getAIClient();

  // Build enhanced prompt based on options
  let examContext = 'an Indian competitive exam (like UPSC CSE Prelims)';
  let languageInstruction = '';
  let styleInstruction = '';
  let caInstruction = '';
  let specialInstructions = '';

  if (advancedOptions) {
    if (advancedOptions.examType) {
      examContext = `the ${advancedOptions.examType} exam`;
    }

    if (advancedOptions.language && advancedOptions.language !== 'English') {
      if (advancedOptions.language === 'Bilingual (Eng+Hindi)') {
        languageInstruction = '\nProvide questions in both English and Hindi (bilingual format).';
      } else {
        languageInstruction = `\nGenerate questions in ${advancedOptions.language} language.`;
      }
    }

    if (advancedOptions.questionStyle) {
      const styleMap: Record<string, string> = {
        'conceptual': 'Focus on conceptual understanding questions that test deep knowledge.',
        'factual': 'Focus on factual/memory-based questions with specific data points.',
        'analytical': 'Focus on analytical questions requiring application of concepts.',
        'pyq': 'Style questions like Previous Year Question papers - with statement-based options (Consider the following statements, Which of the above is/are correct?).',
        'mixed': 'Include a mix of factual, conceptual, and analytical questions.'
      };
      styleInstruction = `\n${styleMap[advancedOptions.questionStyle] || ''}`;
    }

    if (advancedOptions.includeCurrentAffairs && advancedOptions.yearRange) {
      caInstruction = `\nInclude current affairs context from ${advancedOptions.yearRange}. Reference recent events, schemes, and developments.`;
    }

    if (advancedOptions.specificInstructions) {
      specialInstructions = `\nSpecial Instructions: ${advancedOptions.specificInstructions}`;
    }
  }

  const prompt = `
    Generate exactly ${count} multiple choice questions (MCQs) for ${examContext}.
    Subject: "${subject}"
    Topic: "${topic}"
    Difficulty level: ${difficulty}
    ${languageInstruction}
    ${styleInstruction}
    ${caInstruction}
    ${specialInstructions}
    
    IMPORTANT RULES:
    1. Each question must have exactly 4 options labeled A, B, C, D.
    2. Provide a detailed 2-3 line explanation for each answer.
    3. Make questions exam-relevant and factually accurate.
    4. Vary the correct answers across A, B, C, D (don't make all answers the same option).
    
    Return a valid JSON array of objects with these exact fields:
    - text: The question stem (string)
    - options: Array of 4 objects with 'id' (A, B, C, D) and 'text' (string)
    - correctOptionId: The correct option id (A, B, C, or D)
    - explanation: Detailed explanation (string)
    - difficulty: "${difficulty}"
  `;

  if (DEBUG) console.log('Prompt length:', prompt.length, 'chars');

  try {
    if (DEBUG) console.log('Calling Gemini API...');

    // Wrap API call with 30 second timeout
    const apiCall = ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.ARRAY,
          items: {
            type: Type.OBJECT,
            properties: {
              text: { type: Type.STRING },
              options: {
                type: Type.ARRAY,
                items: {
                  type: Type.OBJECT,
                  properties: {
                    id: { type: Type.STRING },
                    text: { type: Type.STRING }
                  }
                }
              },
              correctOptionId: { type: Type.STRING },
              explanation: { type: Type.STRING },
              difficulty: { type: Type.STRING }
            }
          }
        }
      }
    });

    // Wait for response (no timeout - wait as long as needed)
    if (DEBUG) console.log('Waiting for AI response... (this may take a while for complex prompts)');
    const response = await apiCall;

    if (DEBUG) console.log('Response received!');

    let jsonText = response.text;
    if (!jsonText) throw new Error("No response from AI");

    if (DEBUG) console.log('Response length:', jsonText.length, 'chars');

    // Clean up response if needed
    jsonText = jsonText.trim();

    // Remove markdown code blocks if present
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.replace(/^```json\n?/, '').replace(/\n?```$/, '');
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.replace(/^```\n?/, '').replace(/\n?```$/, '');
    }

    let rawData;
    try {
      rawData = JSON.parse(jsonText);
    } catch (parseError) {
      // Try to salvage malformed JSON
      console.error('JSON Parse Error, attempting cleanup...', parseError);

      // Try to fix common issues
      const fixedJson = jsonText
        .replace(/,\s*}/g, '}')  // Remove trailing commas before }
        .replace(/,\s*]/g, ']')  // Remove trailing commas before ]
        .replace(/\\'/g, "'")     // Fix escaped single quotes
        .replace(/[\x00-\x1F\x7F]/g, ' '); // Remove control characters

      try {
        rawData = JSON.parse(fixedJson);
      } catch {
        throw new Error(`Invalid JSON response from AI. Please try again with fewer questions.`);
      }
    }

    // Validate and map to our internal Question type
    if (!Array.isArray(rawData)) {
      throw new Error('AI returned invalid format. Expected an array of questions.');
    }

    const questions: Question[] = rawData.map((q: any, index: number) => ({
      id: `ai-${Date.now()}-${index}`,
      text: q.text || `Question ${index + 1}`,
      options: Array.isArray(q.options) ? q.options : [
        { id: 'A', text: 'Option A' },
        { id: 'B', text: 'Option B' },
        { id: 'C', text: 'Option C' },
        { id: 'D', text: 'Option D' }
      ],
      correctOptionId: q.correctOptionId || 'A',
      explanation: q.explanation || 'No explanation provided.',
      difficulty: q.difficulty || difficulty,
      subject: subject
    }));

    return questions;

  } catch (error: any) {
    console.error("AI Generation Failed:", error);

    // Re-throw with better message
    if (error.message?.includes('API_KEY') || error.message?.includes('apiKey')) {
      throw new Error('API Key not configured. Please set GEMINI_API_KEY.');
    }
    if (error.message?.includes('quota') || error.message?.includes('429')) {
      throw new Error('API rate limit reached. Please wait a moment and try again.');
    }
    if (error.message?.includes('JSON') || error.message?.includes('parse')) {
      throw new Error('AI response was malformed. Try reducing question count.');
    }

    throw error;
  }
};

export const analyzeUserPerformance = async (attempts: Attempt[], userName: string): Promise<AIAnalysis> => {
  const ai = getAIClient();

  // Prepare data summary for AI
  const summary = attempts.map(a => ({
    exam: a.examTitle,
    score: `${a.score.toFixed(1)}`,
    accuracy: `${Math.round((a.correctCount / (a.correctCount + a.wrongCount || 1)) * 100)}%`,
    date: new Date(a.createdAt).toLocaleDateString()
  })).slice(0, 10); // Last 10 attempts

  const prompt = `
    You are an expert exam coach. Analyze the recent performance of a student named ${userName}.
    
    Here is their recent test history:
    ${JSON.stringify(summary)}
    
    Provide a JSON response with:
    1. 'strengths': 2-3 bullet points on what they are doing well.
    2. 'weaknesses': 2-3 bullet points on areas of concern (e.g. low accuracy, inconsistency).
    3. 'recommendations': 3 actionable study tips or topics to focus on.
    4. 'motivation': A short, 1-line motivating quote or message personalized to their performance.
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            strengths: { type: Type.ARRAY, items: { type: Type.STRING } },
            weaknesses: { type: Type.ARRAY, items: { type: Type.STRING } },
            recommendations: { type: Type.ARRAY, items: { type: Type.STRING } },
            motivation: { type: Type.STRING }
          }
        }
      }
    });

    const jsonText = response.text;
    if (!jsonText) throw new Error("No response from AI");

    return JSON.parse(jsonText) as AIAnalysis;

  } catch (error) {
    console.error("AI Analysis Failed:", error);
    throw error;
  }
};